import React, { useState } from "react";
import {
 Modal,
 Box,
 Button,
 Typography,
 Card,
 CardContent,
 useTheme,
 useMediaQuery,
 Divider,
 styled,
} from "@mui/material";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";

type ResultProps = {
 result: {
  name: string;
  email: string;
  answers: {
   question: string;
   answer: string;
   score: number;
   timeSpent: string;
  }[];
  totalScore: number;
  duration: string;
 };
};

const logos = [
 {
  src: `/assets/logo-smartx.webp`,
  alt: "Smartx Academy",
 },
 {
  src: `/assets/logo-ifa.png`,
  alt: "IFA Academy",
  preview: false,
 },
];

const getPersonalityType = (score: number) => {
 if (score <= 12) {
  return {
   title: "Bạn thiên về phản ứng cảm tính",
   description:
    "Bạn thường đưa ra quyết định dựa trên cảm xúc cá nhân, trực giác hoặc ấn tượng ban đầu.",
   color: "#64b5f6",
  };
 } else if (score <= 20) {
  return {
   title: "Bạn thuộc nhóm trung lập – an toàn",
   description:
    "Bạn có xu hướng giữ cân bằng giữa cảm xúc và lý trí. Quyết định của bạn thường mang tính thận trọng và có suy nghĩ.",
   color: "#64b5f6",
  };
 } else {
  return {
   title: "Bạn là người phản biện có chiến lược",
   description:
    "Bạn phân tích kỹ càng trước khi hành động, suy nghĩ phản biện và đưa ra quyết định dựa trên lý trí và dữ liệu.",
   color: "#64b5f6",
  };
 }
};

export default function Result({ result }: ResultProps) {
 const [showDetails, setShowDetails] = useState(false);
 const theme = useTheme();
 const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
 const handleShowDetails = () => setShowDetails(true);
 const handleCloseDetails = () => setShowDetails(false);

 return (
  <Box
   maxWidth={500}
   mx='auto'
   p={isMobile ? 2 : 4}
   mt={4}
   borderRadius={3}
   boxShadow={4}
   bgcolor='background.paper'
  >
   <Typography
    variant='h5'
    sx={{ textAlign: "center", mb: 3, fontWeight: 600 }}
   >
    Kết quả đánh giá
   </Typography>

   <Box mb={3}>
    <Typography variant='subtitle1'>
     <strong>Tên:</strong> {result.name}
    </Typography>
    <Typography variant='subtitle1'>
     <strong>Email:</strong> {result.email}
    </Typography>
    <Typography variant='subtitle1'>
     <strong>Tổng điểm:</strong> {result.totalScore}
    </Typography>
    <Typography variant='subtitle1'>
     <strong>Thời gian làm:</strong> {result.duration}
    </Typography>
   </Box>
   <Box
    mt={2}
    mb={2}
    p={2}
    borderRadius={2}
    bgcolor={getPersonalityType(result.totalScore).color}
    color='white'
   >
    <Typography variant='subtitle1' fontWeight={700}>
     {getPersonalityType(result.totalScore).title}
    </Typography>
    <Typography variant='body2'>
     {getPersonalityType(result.totalScore).description}
    </Typography>
   </Box>

   <Button
    variant='contained'
    color='primary'
    fullWidth
    sx={{ textTransform: "none", fontWeight: 600, mb: 2 }}
    onClick={handleShowDetails}
   >
    Xem chi tiết kết quả
   </Button>

   <Modal
    open={showDetails}
    onClose={handleCloseDetails}
    aria-labelledby='question-details-modal'
   >
    <Box
     sx={{
      position: "absolute",
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
      bgcolor: "background.paper",
      boxShadow: 24,
      p: 3,
      width: "90%",
      maxWidth: 500,
      borderRadius: 2,
      maxHeight: "90vh",
      overflowY: "auto",
     }}
    >
     <Typography
      variant='h6'
      sx={{ marginBottom: 2, textAlign: "center", fontWeight: 600 }}
     >
      Chi tiết kết quả
     </Typography>

     {result.answers.map((answer, index) => (
      <Card
       key={index}
       variant='outlined'
       sx={{ mb: 2, borderRadius: 2, boxShadow: 1 }}
      >
       <CardContent>
        <Typography variant='subtitle1' sx={{ fontWeight: 600 }}>
         Câu {index + 1}: {answer.question}
        </Typography>
        <Divider sx={{ my: 1 }} />
        <Typography variant='body2'>
         <strong>Câu trả lời:</strong> {answer.answer}
        </Typography>
        <Typography variant='body2'>
         <strong>Điểm:</strong> {answer.score}
        </Typography>
        <Typography variant='body2'>
         <strong>Thời gian:</strong> {answer.timeSpent}
        </Typography>
       </CardContent>
      </Card>
     ))}

     <Button
      variant='contained'
      color='secondary'
      size='small'
      fullWidth
      sx={{ mt: 2, backgroundColor: "#e57373" }}
      onClick={handleCloseDetails}
     >
      Đóng
     </Button>
    </Box>
   </Modal>

   <Box display='flex' alignItems='center' mb={2}>
    <SupportAgentIcon sx={{ mr: 1 }} color='action' />
    <Typography variant='body2' color='textSecondary'>
     Nếu bạn cần hỗ trợ hoặc cho rằng có sai sót trong kết quả, vui lòng liên hệ
     với quản trị viên hoặc bộ phận phụ trách để được giải đáp.
    </Typography>
   </Box>

   <Box display='flex' flexDirection='column' alignItems='start' sx={{ mb: 2 }}>
    <Typography variant='body2' color='textSecondary'>
     📧 Email hỗ trợ: <a href='mailto:xlms@smartx.edu.vn'>xlms@smartx.edu.vn</a>
    </Typography>
    <Typography variant='body2' color='textSecondary'>
     📞 Hotline: <a href='tel:+0942668182'>0942 66 81 82</a>
    </Typography>
    <Typography variant='body2' color='textSecondary'>
     💬 Chat trực tuyến:
     <a href='https://zalo.me/1139950019296328489'>Truy cập tại đây</a>
    </Typography>
   </Box>

   <Typography variant='body2' color='textSecondary' fontStyle='italic'>
    Chúng tôi cảm ơn bạn đã dành thời gian tham gia bài thi, và chúc bạn đạt
    được những thành tích tốt đẹp trong hành trình học tập của mình.
   </Typography>
   <FooterBox>
    <Typography className='desc'>
     <strong>SmartX Academy</strong> là hệ thống đào tạo và quản trị trực tuyến
     thế hệ mới, được phát triển bởi{" "}
     <strong>Viện Quản trị và Tài chính (IFA)</strong>. Nền tảng này được xây
     dựng nhằm nâng cao năng lực quản lý và phát triển đội ngũ thông qua các
     chương trình đào tạo hiện đại và linh hoạt.
    </Typography>
    <Box className='logo-footer' style={{ marginBottom: 20 }}>
     {logos.map((logo, index) => (
      <Box
       component='img'
       src={logo.src}
       alt={logo.alt}
       className='logo-smartx logo'
       sx={{
        cursor: "pointer",
        height: 50,
       }}
      />
     ))}
    </Box>
   </FooterBox>
  </Box>
 );
}

const FooterBox = styled(Box)`
 display: flex;
 flex-direction: column;
 align-items: center;
 gap: 10px;
 margin-top: 50px;
 .desc {
  font-size: 14px;
  color: #4f4f4f;
  /* max-width: 600px; */
  text-align: center;
  font-weight: 600;
 }
 .logo-footer {
  display: flex;
  align-items: end;
  gap: 20px;
  .logo {
   width: 100px;
  }
 }
`;
